import {
  PlanEntryDetailsResponseCheckoutEvent,
  PlanEntryDetailsResponseItem,
  PlanEntryDetailsResponseItemDriver,
  PlanEntryDetailsResponseItemPassenger,
  PlanEntryDetailsResponseNodeExclusion,
  PlanEntryDetailsResponseNodeExclusions,
  PlanEntryDetailsResponseRideNode,
  PlanEntryDetailsResponseRideNodeMeta,
  PlanEntryDetailsResponseSolvedOrder,
  PlanEntryDetailsResponseSolvedOrderCargoCompany,
  PlanEntryDetailsResponseSolvedOrderRide,
  PlanEntryDetailsResponseSolvedOrderTransportingOrder,
  PlanEntryDetailsResponseSolvedOrderTransportingOrderCargoOrder,
} from "../../../api/raily/plan-entry/details/plan-entry-details.response";
import uuidService from "../../../utils/uuid/uuid.service";
import PlanEntryDetails, {
  PlanEntryDetailsCheckoutEvent,
  PlanEntryDetailsDriver,
  PlanEntryDetailsNodeExclusion,
  PlanEntryDetailsNodeExclusions,
  PlanEntryDetailsPassenger,
  PlanEntryDetailsRideNode,
  PlanEntryDetailsRideNodeMeta,
  PlanEntryDetailsSolvedOrder,
  PlanEntryDetailsSolvedOrderCargoCompany,
  PlanEntryDetailsSolvedOrderRide,
  PlanEntryDetailsSolvedOrderTransportingOrder,
  PlanEntryDetailsSolvedOrderTransportingOrderCargoOrder,
} from "./plan-entry-details";

const createSolvedOrderRideNodeMeta = (
  meta: PlanEntryDetailsResponseRideNodeMeta,
): PlanEntryDetailsRideNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createSolvedOrderRideNode = (
  node: PlanEntryDetailsResponseRideNode,
): PlanEntryDetailsRideNode => {
  return {
    uuid: node.meta.id,
    displayName: node.displayName,
    haltingTime: node.haltingTime,
    lat: node.lat,
    lon: node.lon,
    meta: createSolvedOrderRideNodeMeta(node.meta),
    time: node.time,
    onboardingPassengersUuids: node.meta.cargoEnter.subjects.map(
      (subject) => subject.id,
    ),
    outboardingPassengersUuids: node.meta.cargoExit.subjects.map(
      (subject) => subject.id,
    ),
    estimatedTime: node.meta.estimatedTime,
  };
};

const createSolvedOrderRideNodes = (
  nodes: PlanEntryDetailsResponseRideNode[],
): PlanEntryDetailsRideNode[] => {
  return nodes.map(createSolvedOrderRideNode);
};

const createSolvedOrderRide = (
  ride: PlanEntryDetailsResponseSolvedOrderRide,
): PlanEntryDetailsSolvedOrderRide => {
  return {
    seq: createSolvedOrderRideNodes(ride.seq),
  };
};

const createSolvedOrderTransportingOrderCargoOrder = (
  cargoOrder: PlanEntryDetailsResponseSolvedOrderTransportingOrderCargoOrder,
): PlanEntryDetailsSolvedOrderTransportingOrderCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
  };
};

const createSolvedOrderTransportingOrder = (
  transportingOrder: PlanEntryDetailsResponseSolvedOrderTransportingOrder,
): PlanEntryDetailsSolvedOrderTransportingOrder => {
  return {
    cargoOrder: createSolvedOrderTransportingOrderCargoOrder(
      transportingOrder.cargoOrder,
    ),
  };
};

const createSolvedOrderCargoCompany = (
  cargoCompany: PlanEntryDetailsResponseSolvedOrderCargoCompany,
): PlanEntryDetailsSolvedOrderCargoCompany => {
  return {
    displayName: cargoCompany.displayName,
  };
};

const createSolvedOrder = (
  solvedOrder: PlanEntryDetailsResponseSolvedOrder,
): PlanEntryDetailsSolvedOrder => {
  return {
    cargoCompany: createSolvedOrderCargoCompany(solvedOrder.cargoCompany),
    ride: createSolvedOrderRide(solvedOrder.ride),
    transportingOrder: createSolvedOrderTransportingOrder(
      solvedOrder.transportingOrder,
    ),
    excludeHighway: !solvedOrder.forceAllowToll,
  };
};

const createSolvedOrders = (
  solvedOrders: PlanEntryDetailsResponseSolvedOrder[],
): PlanEntryDetailsSolvedOrder[] => {
  return solvedOrders.map(createSolvedOrder);
};

const createNodeExclusionsItem = (
  nodeExclusionsItem: PlanEntryDetailsResponseNodeExclusion,
): PlanEntryDetailsNodeExclusion => {
  return {
    excludeApproaching: nodeExclusionsItem.excludeApproaching,
    excludeInside: nodeExclusionsItem.excludeInside,
    excludeReturning: nodeExclusionsItem.excludeReturning,
    nodeIds: nodeExclusionsItem.nodeIds,
  };
};

const createNodeExclusionsItems = (
  nodeExclusionsItems: PlanEntryDetailsResponseNodeExclusion[],
): PlanEntryDetailsNodeExclusion[] => {
  return nodeExclusionsItems.map(createNodeExclusionsItem);
};

const createNodeExclusions = (
  nodeExclusions: PlanEntryDetailsResponseNodeExclusions,
): PlanEntryDetailsNodeExclusions => {
  return {
    exclusions: createNodeExclusionsItems(nodeExclusions.exclusions),
  };
};

const createCheckoutEvent = (
  checkoutsEvent: PlanEntryDetailsResponseCheckoutEvent,
): PlanEntryDetailsCheckoutEvent => {
  return {
    achievedAt: checkoutsEvent.achievedAt,
    createdAt: checkoutsEvent.createdAt,
    createdByName: checkoutsEvent.createdByName,
    haltingTime: checkoutsEvent.haltingTime,
    highwayCharge: checkoutsEvent.highwayCharge,
    nodeId: checkoutsEvent.nodeId,
  };
};

const createCheckoutEvents = (
  checkoutsEvents: PlanEntryDetailsResponseCheckoutEvent[],
): PlanEntryDetailsCheckoutEvent[] => {
  return checkoutsEvents.map(createCheckoutEvent);
};

const createPassenger = (
  responsePassenger: PlanEntryDetailsResponseItemPassenger,
): PlanEntryDetailsPassenger => {
  return {
    uuid: responsePassenger.passengerId ?? uuidService.generate(),
    firstName: responsePassenger.firstName,
    lastName: responsePassenger.lastName,
    phoneNumber: {
      dialingCode: responsePassenger.phonePrefix,
      number: responsePassenger.phoneNo,
    },
  };
};

const createPassengers = (
  responsePassengers: PlanEntryDetailsResponseItemPassenger[],
): PlanEntryDetailsPassenger[] => {
  return responsePassengers.map(createPassenger);
};

const createDriver = (
  responseDriver: PlanEntryDetailsResponseItemDriver,
): PlanEntryDetailsDriver => {
  return {
    uuid: responseDriver.id,
    firstName: responseDriver.firstName,
    lastName: responseDriver.lastName,
  };
};

const create = (
  responseItem: PlanEntryDetailsResponseItem,
): PlanEntryDetails => {
  return {
    checkoutEvents: createCheckoutEvents(responseItem.checkoutEvents),
    endNode: createSolvedOrderRideNode(responseItem.endNode),
    humanId: responseItem.humanId,
    id: responseItem.id,
    nodeExclusions: createNodeExclusions(responseItem.nodeExclusions),
    solvedOrders: createSolvedOrders(responseItem.solvedOrders),
    startNode: createSolvedOrderRideNode(responseItem.startNode),
    passengers: createPassengers(responseItem.passengers),
    driver: createDriver(responseItem.driver),
  };
};

const cargoOrderNodeExclusionFactory = { create };

export default cargoOrderNodeExclusionFactory;
